import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { uploadFile } from '../../utils/api';
import toast from 'react-hot-toast';
import { LANGUAGES, CONTRIBUTOR_ROLES, ARTIST_ROLES, WORK_TYPES, makeDefaultContributors, makeDefaultArtists } from '../../utils/constants';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, Pencil, Save, X, Plus, Trash2, Upload, UserPlus, Globe, Instagram, Facebook, Youtube, Music2, Image, Send } from 'lucide-react';

// ─── Modal completo artista (identico al frontend utente, tema dark, endpoint admin) ──
function AdminArtistModal({ userId, onCreated, onClose }) {
  const [form, setForm] = useState({ name: '', spotify_uri: '', apple_music_id: '', photo_path: '', biography: '',
    link_instagram: '', link_facebook: '', link_youtube: '', link_tiktok: '', link_website: '' });
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const { data } = await uploadFile(`/uploads/artwork?for_user=${userId}`, f, 'artwork');
      setForm(p => ({ ...p, photo_path: data.file.filename }));
      setPhotoPreview(URL.createObjectURL(f));
    } catch (err) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nome obbligatorio');
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/artists`, form);
      toast.success(`Artista "${data.artist.name}" creato!`);
      onCreated(data.artist);
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Errore creazione artista'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] px-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-lg">Nuovo Artista</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white"><X size={18}/></button>
        </div>
        <p className="text-xs text-yellow-400 mb-3">Verrà creato nel profilo dell'utente proprietario della release.</p>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gray-700 border-2 border-dashed border-gray-600 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {uploading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"/> : photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover"/> : <Image size={24} className="text-gray-500"/>}
            </div>
            <div>
              <input type="file" accept="image/*" className="hidden" id="adm-rv-artist-photo" onChange={handlePhoto}/>
              <label htmlFor="adm-rv-artist-photo" className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg cursor-pointer hover:bg-gray-600 inline-flex items-center gap-1.5 text-xs"><Upload size={12}/>Foto</label>
              <p className="text-[10px] text-gray-500 mt-1">JPG/PNG, 500×500px</p>
              {photoPreview && <button onClick={() => { setForm(p => ({ ...p, photo_path: '' })); setPhotoPreview(null); }} className="text-[10px] text-red-400 hover:underline mt-1">Rimuovi</button>}
            </div>
          </div>
          <div><label className="block text-sm font-medium text-gray-300 mb-1">Nome Artista *</label><input className="input-field-dark w-full" autoFocus value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Nome artista..."/></div>
          <div><label className="block text-sm font-medium text-gray-300 mb-1">Biografia</label><textarea className="input-field-dark w-full h-20 resize-y text-xs" value={form.biography} onChange={e => setForm(p => ({ ...p, biography: e.target.value }))} placeholder="Breve biografia..."/></div>
          <div className="border-t border-gray-700 pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Piattaforme Musicali</p>
            <div className="space-y-2">
              <div><label className="block text-[10px] text-gray-500 mb-1">Spotify URI</label><input className="input-field-dark w-full" value={form.spotify_uri} onChange={e => setForm(p => ({ ...p, spotify_uri: e.target.value }))} placeholder="spotify:artist:..."/></div>
              <div><label className="block text-[10px] text-gray-500 mb-1">Apple Music ID</label><input className="input-field-dark w-full" value={form.apple_music_id} onChange={e => setForm(p => ({ ...p, apple_music_id: e.target.value }))} placeholder="https://music.apple.com/artist/..."/></div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Link & Social</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Globe size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_website} onChange={e => setForm(p => ({ ...p, link_website: e.target.value }))} placeholder="https://..."/></div>
              <div className="flex items-center gap-2"><Instagram size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_instagram} onChange={e => setForm(p => ({ ...p, link_instagram: e.target.value }))} placeholder="https://instagram.com/..."/></div>
              <div className="flex items-center gap-2"><Facebook size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_facebook} onChange={e => setForm(p => ({ ...p, link_facebook: e.target.value }))} placeholder="https://facebook.com/..."/></div>
              <div className="flex items-center gap-2"><Youtube size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_youtube} onChange={e => setForm(p => ({ ...p, link_youtube: e.target.value }))} placeholder="https://youtube.com/@..."/></div>
              <div className="flex items-center gap-2"><Music2 size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_tiktok} onChange={e => setForm(p => ({ ...p, link_tiktok: e.target.value }))} placeholder="https://tiktok.com/@..."/></div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600">Annulla</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-yellow-500 text-black rounded-lg text-sm font-bold hover:bg-yellow-400 disabled:opacity-50">{saving ? 'Salvataggio...' : 'Crea Artista'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Riga artista con autocomplete ────────────────────────────────────────────
function ArtistRow({ idx, artist, userArtists, onUpdate, onRemove, canRemove, onCreateNew }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const query = (artist.name || '').toLowerCase();
  const filtered = query.length >= 1
    ? userArtists.filter(a => a.name.toLowerCase().includes(query))
    : userArtists;

  return (
    <div className="flex items-center gap-2 mb-2">
      <select className="input-field-dark text-xs w-40" value={artist.role}
        onChange={e => onUpdate(idx, 'role', e.target.value)}>
        {ARTIST_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <div className="flex-1 relative">
        <div className="relative flex items-center gap-1">
          <input
            className={`input-field-dark text-xs w-full ${artist.artist_id ? 'border-green-700 bg-green-900/10' : ''}`}
            placeholder="Nome artista..."
            value={artist.name || ''}
            onChange={e => { onUpdate(idx, 'name', e.target.value); onUpdate(idx, 'artist_id', null); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          />
          {artist.artist_id && (
            <span className="absolute right-2 text-[9px] text-green-400 font-bold pointer-events-none">✓</span>
          )}
        </div>
        {showSuggestions && (
          <div className="absolute z-40 top-full left-0 right-0 bg-gray-700 border border-gray-600 rounded-lg shadow-xl mt-0.5 max-h-40 overflow-y-auto">
            {filtered.map(a => (
              <button key={a.id}
                onMouseDown={() => { onUpdate(idx, 'name', a.name); onUpdate(idx, 'artist_id', a.id); setShowSuggestions(false); }}
                className="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-600 flex items-center gap-2 border-b border-gray-600/50 last:border-0">
                {a.photo_path
                  ? <img src={`/uploads/artwork/${a.photo_path}`} className="w-5 h-5 rounded-full object-cover flex-shrink-0" alt=""/>
                  : <div className="w-5 h-5 rounded-full bg-yellow-800 text-yellow-300 text-[9px] flex items-center justify-center flex-shrink-0 font-bold">{a.name?.[0]}</div>}
                <span className="flex-1 truncate">{a.name}</span>
                {a.spotify_uri && <span className="text-[9px] text-green-400">S</span>}
                {a.apple_music_id && <span className="text-[9px] text-pink-400">A</span>}
              </button>
            ))}
            <button onMouseDown={onCreateNew}
              className="w-full text-left px-3 py-2 text-[10px] text-yellow-400 hover:bg-gray-600 flex items-center gap-1.5 border-t border-gray-600">
              <UserPlus size={10}/> {artist.name ? `Crea "${artist.name}" come nuovo artista` : 'Crea nuovo artista...'}
            </button>
          </div>
        )}
      </div>
      {canRemove && (
        <button onClick={() => onRemove(idx)} className="p-1 text-gray-500 hover:text-red-400 flex-shrink-0"><X size={12}/></button>
      )}
    </div>
  );
}

// ─── Form modifica traccia ────────────────────────────────────────────────────
function TrackEditForm({ track, genres, userId, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: track.title || '',
    version_label: track.version_label || track.version_type || '',
    work_type: track.work_type || '',
    language: track.language || '',
    genre1_id: track.genre1_id || '',
    genre2_id: track.genre2_id || '',
    explicit_content: track.explicit_content === 1 ? true : track.explicit_content === 0 ? false : null,
    isrc: track.isrc || '',
    lyrics: track.lyrics || '',
    copyright_text: track.copyright_text || '',
    copyright_year: track.copyright_year || '',
    artists: track.artists?.length ? track.artists.map(a => ({ ...a })) : [{ name: '', role: 'primary', artist_id: null }],
    contributors: track.contributors?.length ? track.contributors.map(c => ({ ...c })) : makeDefaultContributors(),
    ai_generated: !!track.ai_generated,
    instrumental: !!track.instrumental
  });

  const [userArtists, setUserArtists] = useState([]);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [pendingArtistIdx, setPendingArtistIdx] = useState(null);

  useEffect(() => {
    if (!userId) return;
    api.get(`/admin/users/${userId}/artists`)
      .then(({ data }) => setUserArtists(data.artists || []))
      .catch(() => {});
  }, [userId]);

  const updArtist = (i, k, v) => {
    const a = [...form.artists]; a[i] = { ...a[i], [k]: v }; setForm(p => ({ ...p, artists: a }));
  };
  const addArtist = () => setForm(p => ({ ...p, artists: [...p.artists, { name: '', role: 'featuring', artist_id: null }] }));
  const rmArtist = (i) => setForm(p => ({ ...p, artists: p.artists.filter((_, x) => x !== i) }));

  const updContrib = (i, k, v) => { const c = [...form.contributors]; c[i] = { ...c[i], [k]: v }; setForm(p => ({ ...p, contributors: c })); };
  const addContrib = () => setForm(p => ({ ...p, contributors: [...p.contributors, { name: '', role: 'arranger' }] }));
  const rmContrib = (i) => setForm(p => ({ ...p, contributors: p.contributors.filter((_, x) => x !== i) }));

  const handleArtistCreated = (newArtist, forIdx) => {
    setUserArtists(prev => [...prev, newArtist]);
    if (forIdx !== null && forIdx !== undefined) {
      updArtist(forIdx, 'name', newArtist.name);
      updArtist(forIdx, 'artist_id', newArtist.id);
    }
  };

  return (
    <div className="mt-3 p-4 bg-gray-800 rounded-lg border border-yellow-800">
      {showArtistModal && (
        <AdminArtistModal
          userId={userId}
          onCreated={(artist) => handleArtistCreated(artist, pendingArtistIdx)}
          onClose={() => { setShowArtistModal(false); setPendingArtistIdx(null); }}
        />
      )}

      <p className="text-xs text-yellow-400 font-bold uppercase mb-3">Modifica Traccia</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label className="block text-[10px] text-gray-500 mb-1">Titolo</label><input className="input-field-dark text-xs" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}/></div>
        <div><label className="block text-[10px] text-gray-500 mb-1">Tipo Lavoro *</label>
          <select className="input-field-dark text-xs" value={form.work_type} onChange={e => setForm(p => ({ ...p, work_type: e.target.value }))}>
            {WORK_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </div>
        <div><label className="block text-[10px] text-gray-500 mb-1">ISRC</label><input className="input-field-dark text-xs" value={form.isrc} onChange={e => setForm(p => ({ ...p, isrc: e.target.value.toUpperCase() }))} maxLength={12}/></div>
        <div><label className="block text-[10px] text-gray-500 mb-1">Versione</label><input className="input-field-dark text-xs" value={form.version_label} onChange={e => setForm(p => ({ ...p, version_label: e.target.value }))} placeholder="Remix, Acoustic..."/></div>
        <div><label className="block text-[10px] text-gray-500 mb-1">Lingua</label>
          <select className="input-field-dark text-xs" value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}>
            <option value="">-</option>{LANGUAGES.map(l => <option key={l}>{l}</option>)}
          </select>
        </div>
        <div><label className="block text-[10px] text-gray-500 mb-1">Genere 1</label>
          <select className="input-field-dark text-xs" value={form.genre1_id} onChange={e => setForm(p => ({ ...p, genre1_id: e.target.value }))}>
            <option value="">-</option>{genres.map(g => <optgroup key={g.id} label={g.name}>{g.subgenres?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>)}
          </select>
        </div>
        <div><label className="block text-[10px] text-gray-500 mb-1">Genere 2</label>
          <select className="input-field-dark text-xs" value={form.genre2_id} onChange={e => setForm(p => ({ ...p, genre2_id: e.target.value }))}>
            <option value="">-</option>{genres.map(g => <optgroup key={g.id} label={g.name}>{g.subgenres?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] text-gray-500 mb-1">Contenuto Esplicito *</label>
          <div className="flex gap-3">
            <label className={`cursor-pointer px-4 py-2 rounded-lg border-2 text-xs ${form.explicit_content === true ? 'border-red-500 bg-red-900/30 font-bold text-red-300' : 'border-gray-600 text-gray-400'}`}>
              <input type="radio" name="adm-ex" checked={form.explicit_content === true} onChange={() => setForm(p => ({ ...p, explicit_content: true }))} className="hidden"/>Esplicito
            </label>
            <label className={`cursor-pointer px-4 py-2 rounded-lg border-2 text-xs ${form.explicit_content === false ? 'border-green-500 bg-green-900/30 font-bold text-green-300' : 'border-gray-600 text-gray-400'}`}>
              <input type="radio" name="adm-ex" checked={form.explicit_content === false} onChange={() => setForm(p => ({ ...p, explicit_content: false }))} className="hidden"/>Non Esplicito
            </label>
          </div>
        </div>
      </div>

      {/* Artisti con autocomplete */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-gray-500 font-bold uppercase">Artisti</p>
        {userArtists.length > 0 && <span className="text-[9px] text-gray-600">{userArtists.length} artisti utente</span>}
      </div>
      {form.artists.map((a, i) => (
        <ArtistRow key={i} idx={i} artist={a} userArtists={userArtists}
          onUpdate={updArtist} onRemove={rmArtist} canRemove={form.artists.length > 1}
          onCreateNew={() => { setPendingArtistIdx(i); setShowArtistModal(true); }}
        />
      ))}
      <div className="flex gap-4 mb-3">
        <button onClick={addArtist} className="text-[10px] text-yellow-400 hover:underline">+ Aggiungi Artista</button>
        <button onClick={() => { setPendingArtistIdx(null); setShowArtistModal(true); }}
          className="text-[10px] text-yellow-600 hover:underline flex items-center gap-1">
          <UserPlus size={9}/> Crea nuovo artista...
        </button>
      </div>

      {/* Contributori */}
      <p className="text-[10px] text-gray-500 font-bold uppercase mb-2">Contributori</p>
      {form.contributors.map((c, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <select className="input-field-dark text-xs w-40" value={c.role} onChange={e => updContrib(i, 'role', e.target.value)}>{CONTRIBUTOR_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}</select>
          <input className="input-field-dark text-xs flex-1" placeholder="Nome..." value={c.name || ''} onChange={e => updContrib(i, 'name', e.target.value)}/>
          {form.contributors.length > 1 && <button onClick={() => rmContrib(i)} className="p-1 text-gray-500 hover:text-red-400"><X size={12}/></button>}
        </div>
      ))}
      <button onClick={addContrib} className="text-[10px] text-yellow-400 hover:underline mb-3 block">+ Aggiungi Contributore</button>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label className="block text-[10px] text-gray-500 mb-1">© Copyright Traccia</label><input className="input-field-dark text-xs" value={form.copyright_text} onChange={e => setForm(p => ({ ...p, copyright_text: e.target.value }))} placeholder="2026 Nome Artista"/></div>
        <div><label className="block text-[10px] text-gray-500 mb-1">Anno ©</label><input className="input-field-dark text-xs" value={form.copyright_year} onChange={e => setForm(p => ({ ...p, copyright_year: e.target.value }))} placeholder="2026"/></div>
      </div>
      <div className="flex flex-wrap gap-4 mb-3">
        <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300"><input type="checkbox" checked={!!form.ai_generated} onChange={e => setForm(p => ({ ...p, ai_generated: e.target.checked }))}/>Creata con AI</label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300"><input type="checkbox" checked={!!form.instrumental} onChange={e => setForm(p => ({ ...p, instrumental: e.target.checked }))}/>Strumentale</label>
      </div>
      <div className="mb-3"><label className="block text-[10px] text-gray-500 mb-1">Testo / Lyrics</label><textarea className="input-field-dark text-xs h-20 resize-y" value={form.lyrics} onChange={e => setForm(p => ({ ...p, lyrics: e.target.value }))}/></div>

      <div className="flex gap-2">
        <button onClick={() => onSave(form)} className="px-4 py-2 bg-green-600 text-white text-xs rounded-lg inline-flex items-center gap-1"><Save size={12}/>Salva Traccia</button>
        <button onClick={onCancel} className="px-4 py-2 bg-gray-700 text-gray-300 text-xs rounded-lg inline-flex items-center gap-1"><X size={12}/>Annulla</button>
      </div>
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function AdminReleaseReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [release, setRelease] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [upc, setUpc] = useState('');
  const [processing, setProcessing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editRelease, setEditRelease] = useState({});
  const [editTrackId, setEditTrackId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [audioUploading, setAudioUploading] = useState({});
  const [releaseArtists, setReleaseArtists] = useState([]);
  const [editReleaseArtists, setEditReleaseArtists] = useState([]);
  const [pageUserArtists, setPageUserArtists] = useState([]);
  const [showReleaseArtistModal, setShowReleaseArtistModal] = useState(false);

  const load = async () => {
    try {
      const [rRes, gRes] = await Promise.all([
        api.get(`/releases/${id}`),
        api.get('/genres').catch(() => ({ data: { genres: [] } }))
      ]);
      setRelease(rRes.data.release);
      setTracks(rRes.data.tracks);
      setReleaseArtists(rRes.data.release_artists || []);
      setEditReleaseArtists((rRes.data.release_artists || []).map(a => ({ name: a.name, role: a.role, artist_id: a.artist_id })));
      setUpc(rRes.data.release.upc || '');
      setGenres(gRes.data.genres || []);
      // Carica artisti dell'utente proprietario
      if (rRes.data.release.user_id) {
        try { const aRes = await api.get(`/admin/users/${rRes.data.release.user_id}/artists`); setPageUserArtists(aRes.data.artists || []); } catch(e){}
      }
      const r = rRes.data.release;
      setEditRelease({
        title: r.title || '', release_type: r.release_type || 'single',
        release_date: r.release_date ? r.release_date.split('T')[0] : '',
        original_release_date: r.original_release_date ? r.original_release_date.split('T')[0] : '',
        copyright_phonographic: r.copyright_phonographic || '',
        copyright_phonographic_year: r.copyright_phonographic_year || '',
        content_id_youtube: r.content_id_youtube ? 1 : 0,
        previously_distributed: r.previously_distributed ? 1 : 0
      });
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const approve = async () => {
    setProcessing(true);
    try {
      const trackIsrcs = {};
      tracks.forEach(t => { if (!t.isrc && t._newIsrc) trackIsrcs[t.id] = t._newIsrc; });
      await api.post(`/releases/${id}/approve`, {
        admin_notes: notes, upc: upc || undefined,
        track_isrcs: Object.keys(trackIsrcs).length > 0 ? trackIsrcs : undefined
      });
      toast.success('Approvata!'); navigate('/admin/releases');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setProcessing(false); }
  };

  const reject = async () => {
    if (!rejectReason) return toast.error('Inserisci il motivo');
    setProcessing(true);
    try { await api.post(`/releases/${id}/reject`, { rejection_reason: rejectReason }); toast.success('Rifiutata'); navigate('/admin/releases'); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setProcessing(false); }
  };

  const saveRelease = async () => {
    setSavingEdit(true);
    try { await api.put(`/admin/releases/${id}`, { ...editRelease, upc, artists: editReleaseArtists }); toast.success('Release aggiornata!'); load(); }
    catch (err) { toast.error('Errore'); }
    finally { setSavingEdit(false); }
  };

  const saveTrack = async (trackId, data) => {
    try { await api.put(`/admin/releases/${id}/tracks/${trackId}`, data); toast.success('Traccia aggiornata!'); setEditTrackId(null); load(); }
    catch (err) { toast.error('Errore salvataggio traccia'); }
  };

  // Upload sostituzione audio traccia
  const handleTrackAudioUpload = async (trackId, file) => {
    setAudioUploading(p => ({ ...p, [trackId]: true }));
    try {
      const { data } = await uploadFile(`/uploads/audio?for_user=${release.user_id}`, file, 'audio');
      await api.put(`/admin/releases/${id}/tracks/${trackId}`, {
        audio_path: data.file.filename,
        audio_format: data.file.format,
        audio_duration_seconds: data.file.duration,
        audio_sample_rate: data.file.sampleRate,
        audio_bit_depth: data.file.bitDepth
      });
      toast.success('Audio sostituito!');
      load();
    } catch (e) { toast.error('Errore upload audio'); }
    finally { setAudioUploading(p => ({ ...p, [trackId]: false })); }
  };

  const deleteRelease = async () => {
    if (!window.confirm('ATTENZIONE: Eliminare questa release e tutte le tracce?')) return;
    try { await api.delete(`/admin/releases/${id}`); toast.success('Eliminata'); navigate('/admin/releases'); }
    catch (e) { toast.error('Errore'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" /></div>;
  if (!release) return null;

  return (
    <div>
      {showReleaseArtistModal && (
        <AdminArtistModal
          userId={release.user_id}
          onCreated={(newArtist) => {
            setPageUserArtists(prev => [...prev, newArtist]);
            setEditReleaseArtists(prev => [...prev, { name: newArtist.name, role: 'primary', artist_id: newArtist.id }]);
            setShowReleaseArtistModal(false);
          }}
          onClose={() => setShowReleaseArtistModal(false)}
        />
      )}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/releases')} className="p-2 hover:bg-gray-800 rounded-lg text-white"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{release.title}</h1>
          <p className="text-sm text-gray-400">{releaseArtists.map(a=>a.name).join(', ') || '—'} · {release.first_name} {release.last_name} · {release.label_name} · {release.release_type}</p>
        </div>
        <button onClick={() => setEditMode(!editMode)}
          className={`px-3 py-1.5 text-xs rounded-lg inline-flex items-center gap-1 ${editMode ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-300'}`}>
          <Pencil size={12} />{editMode ? 'Modifica ON' : 'Modifica'}
        </button>
        <button onClick={deleteRelease} className="px-3 py-1.5 text-xs rounded-lg bg-red-900 text-red-300 hover:bg-red-800 inline-flex items-center gap-1">
          <Trash2 size={12} />Elimina
        </button>
        <span className="text-sm px-3 py-1 rounded-full bg-yellow-600 text-white">{release.status}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card-dark p-6">
            <h2 className="font-bold text-white mb-4">{tracks.length} Tracce</h2>
            {tracks.map((t, ti) => (
              <div key={t.id} className="p-3 bg-gray-900 rounded-lg mb-3">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-6 rounded-full bg-gray-700 text-white text-xs font-bold flex items-center justify-center">{t.track_number}</span>
                  <span className="font-medium text-white flex-1">{t.title}{t.version_type ? ` (${t.version_type})` : ''}</span>
                  {t.explicit_content && <span className="text-[10px] px-1.5 py-0.5 bg-red-900 text-red-300 rounded">EXPLICIT</span>}

                  {/* Audio info */}
                  {t.audio_path ? (
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/40 text-blue-300 rounded whitespace-nowrap">
                      {t.audio_format?.toUpperCase() || '?'} {t.audio_duration_seconds ? `· ${Math.floor(t.audio_duration_seconds/60)}:${String(t.audio_duration_seconds%60).padStart(2,'0')}` : ''} {t.audio_sample_rate ? `· ${t.audio_sample_rate/1000}kHz` : ''}
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-900/40 text-red-400 rounded flex items-center gap-1">
                      <AlertTriangle size={9}/>No audio
                    </span>
                  )}

                  {editMode && (
                    <>
                      <button onClick={() => setEditTrackId(editTrackId === t.id ? null : t.id)}
                        className="text-yellow-400 hover:text-yellow-300" title="Modifica metadati">
                        <Pencil size={12} />
                      </button>
                      {/* Bottone upload/sostituzione audio */}
                      <input type="file" accept=".wav,.flac" className="hidden"
                        id={`adm-audio-${t.id}`}
                        onChange={e => e.target.files[0] && handleTrackAudioUpload(t.id, e.target.files[0])}/>
                      <label htmlFor={`adm-audio-${t.id}`}
                        className="cursor-pointer text-blue-400 hover:text-blue-300"
                        title={t.audio_path ? 'Sostituisci audio WAV/FLAC' : 'Carica audio WAV/FLAC'}>
                        {audioUploading[t.id]
                          ? <div className="w-3 h-3 rounded-full border-b border-blue-400 animate-spin"/>
                          : <Upload size={12}/>}
                      </label>
                    </>
                  )}
                </div>

                {/* Audio Player */}
                {t.audio_path && (
                  <audio controls className="w-full h-8 mt-1" src={`/uploads/audio/${release.user_id}/${t.audio_path}`} preload="none"/>
                )}

                <div className="text-xs text-gray-400 space-y-1">
                  <p>Artisti: {t.artists?.map(a => `${a.name} (${a.role})`).join(', ') || '-'}</p>
                  <p>Genere: {t.genre1_name}{t.subgenre1_name ? ` / ${t.subgenre1_name}` : ''}{t.genre2_name ? ` + ${t.genre2_name}${t.subgenre2_name ? '/' + t.subgenre2_name : ''}` : ''}</p>
                  <p>Lingua: {t.language || '-'} · Contributori: {t.contributors?.map(c => `${c.name} (${c.role})`).join(', ') || '-'}</p>
                  {(t.ai_generated || t.instrumental) && (
                    <div className="flex gap-2 mt-0.5">
                      {t.ai_generated ? <span className="text-[10px] px-1.5 py-0.5 bg-yellow-900/40 text-yellow-400 rounded">AI</span> : null}
                      {t.instrumental ? <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/40 text-blue-300 rounded">Strumentale</span> : null}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className={t.isrc ? "text-green-400" : "text-yellow-400"}>ISRC:</span>
                    <input className="input-field-dark text-xs py-1 px-2 w-40" placeholder="Assegna ISRC"
                      value={t._newIsrc !== undefined ? t._newIsrc : (t.isrc || '')}
                      onChange={e => { const n = [...tracks]; n[ti] = { ...n[ti], _newIsrc: e.target.value.toUpperCase() }; setTracks(n); }}
                      maxLength={12}/>
                    {(t._newIsrc !== undefined ? t._newIsrc : '') !== (t.isrc || '') && (
                      <button
                        onClick={async () => {
                          try {
                            const val = t._newIsrc !== undefined ? t._newIsrc : '';
                            await api.put(`/admin/releases/${id}/tracks/${t.id}`, { isrc: val || null });
                            toast.success(val ? 'ISRC salvato!' : 'ISRC rimosso');
                            load();
                          } catch (e) { toast.error('Errore salvataggio ISRC'); }
                        }}
                        className="px-2 py-0.5 bg-green-700 text-green-200 rounded text-[10px] font-bold hover:bg-green-600">
                        Salva
                      </button>
                    )}
                  </div>
                </div>

                {editTrackId === t.id && (
                  <TrackEditForm
                    track={t} genres={genres}
                    userId={release.user_id}
                    onSave={d => saveTrack(t.id, d)}
                    onCancel={() => setEditTrackId(null)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-dark p-5">
            {release.cover_art_path && (
              <img src={`/uploads/artwork/${release.user_id}/${release.cover_art_path}`} alt=""
                className="w-full aspect-square rounded-lg bg-gray-700 object-cover mb-4"
                onError={e => { e.target.style.display = 'none'; }}/>
            )}
            {editMode ? (
              <div className="space-y-3 text-sm">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Artista/i Release</label>
                  {editReleaseArtists.map((a, ai) => (
                    <div key={ai} className="flex items-center gap-1 mb-1">
                      <select className="input-field-dark text-[10px] w-20 py-1" value={a.role}
                        onChange={e => { const ar = [...editReleaseArtists]; ar[ai] = { ...a, role: e.target.value }; setEditReleaseArtists(ar); }}>
                        <option value="primary">Princ.</option><option value="featuring">Feat.</option><option value="remixer">Remix</option>
                      </select>
                      <select className="input-field-dark text-[10px] flex-1 py-1" value={a.artist_id || ''}
                        onChange={e => { const sel = pageUserArtists.find(u => String(u.id) === e.target.value); const ar = [...editReleaseArtists]; ar[ai] = { ...a, name: sel?.name || '', artist_id: sel?.id || null }; setEditReleaseArtists(ar); }}>
                        <option value="">Seleziona...</option>
                        {pageUserArtists.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      {editReleaseArtists.length > 1 && <button onClick={() => setEditReleaseArtists(editReleaseArtists.filter((_, i) => i !== ai))} className="p-0.5 text-gray-500 hover:text-red-400"><X size={10}/></button>}
                    </div>
                  ))}
                  <div className="flex gap-3">
                    <button onClick={() => setEditReleaseArtists(p => [...p, { name: '', role: 'featuring', artist_id: null }])}
                      className="text-[9px] text-yellow-400 hover:underline">+ Artista</button>
                    <button onClick={() => setShowReleaseArtistModal(true)}
                      className="text-[9px] text-yellow-600 hover:underline flex items-center gap-0.5">
                      <UserPlus size={8}/> Crea nuovo
                    </button>
                  </div>
                </div>
                <div><label className="block text-[10px] text-gray-500">Titolo</label><input className="input-field-dark text-sm" value={editRelease.title} onChange={e => setEditRelease(p => ({ ...p, title: e.target.value }))}/></div>
                <div><label className="block text-[10px] text-gray-500">Tipo</label><select className="input-field-dark" value={editRelease.release_type} onChange={e => setEditRelease(p => ({ ...p, release_type: e.target.value }))}><option value="single">Single</option><option value="ep">EP</option><option value="album">Album</option></select></div>
                <div><label className="block text-[10px] text-gray-500">Data Uscita</label><input type="date" className="input-field-dark" value={editRelease.release_date} onChange={e => setEditRelease(p => ({ ...p, release_date: e.target.value }))}/></div>
                <div><label className="block text-[10px] text-gray-500">Data Uscita Originale</label><input type="date" className="input-field-dark" value={editRelease.original_release_date} onChange={e => setEditRelease(p => ({ ...p, original_release_date: e.target.value }))}/></div>
                <div><label className="block text-[10px] text-gray-500">℗ Fonografico</label><input className="input-field-dark" value={editRelease.copyright_phonographic} onChange={e => setEditRelease(p => ({ ...p, copyright_phonographic: e.target.value }))}/></div>
                <div><label className="block text-[10px] text-gray-500">Anno ℗</label><input className="input-field-dark" value={editRelease.copyright_phonographic_year} onChange={e => setEditRelease(p => ({ ...p, copyright_phonographic_year: e.target.value }))}/></div>
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 text-xs text-gray-300"><input type="checkbox" checked={!!editRelease.content_id_youtube} onChange={e => setEditRelease(p => ({ ...p, content_id_youtube: e.target.checked ? 1 : 0 }))}/>Content ID YT</label>
                  <label className="flex items-center gap-2 text-xs text-gray-300"><input type="checkbox" checked={!!editRelease.previously_distributed} onChange={e => setEditRelease(p => ({ ...p, previously_distributed: e.target.checked ? 1 : 0 }))}/>Già distribuito</label>
                </div>
                <button onClick={saveRelease} disabled={savingEdit}
                  className="w-full py-2 bg-yellow-500 text-black rounded-lg font-medium text-sm inline-flex items-center justify-center gap-1">
                  <Save size={14}/>{savingEdit ? '...' : 'Salva Release'}
                </button>
              </div>
            ) : (
              <div className="space-y-2 text-sm text-gray-300">
                <div className="flex justify-between"><span className="text-gray-500">Artista:</span><span className="text-yellow-400 font-medium">{releaseArtists.map(a=>a.name).join(', ') || '-'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Label:</span><span>{release.label_name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Tipo:</span><span>{release.release_type}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Data:</span><span>{release.release_date ? new Date(release.release_date).toLocaleDateString('it-IT') : '-'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">℗:</span><span>{release.copyright_phonographic || '-'} {release.copyright_phonographic_year || ''}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Content ID:</span><span className={release.content_id_youtube ? 'text-green-400' : ''}>{release.content_id_youtube ? 'SI' : 'NO'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Già distribuito:</span><span>{release.previously_distributed ? 'SI' : 'NO'}</span></div>
              </div>
            )}
          </div>
          <div className="card-dark p-5">
            <label className="block text-xs text-gray-400 mb-1">UPC</label>
            <input className="input-field-dark" value={upc} onChange={e => setUpc(e.target.value)} placeholder="Assegna UPC..."/>
          </div>
          {['in_review', 'draft'].includes(release.status) && (<>
            {/* Solo per bozze: Invia in Revisione o Distribuisci direttamente */}
            {release.status === 'draft' && (
              <div className="card-dark p-5 space-y-3">
                <p className="text-xs text-gray-500">Questa release è una bozza. Puoi:</p>
                <button onClick={async () => {
                  setProcessing(true);
                  try {
                    await api.post(`/admin/releases/${id}/submit-admin`);
                    toast.success('Impostata come distribuita!');
                    load();
                  } catch (e) { toast.error(e.response?.data?.error || 'Errore'); }
                  finally { setProcessing(false); }
                }} disabled={processing}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium inline-flex items-center justify-center gap-2">
                  <Send size={18}/>{processing ? '...' : 'Distribuisci Direttamente'}
                </button>
                <button onClick={async () => {
                  setProcessing(true);
                  try {
                    await api.put(`/admin/releases/${id}`, { status: 'in_review' });
                    toast.success('Inviata in revisione!');
                    load();
                  } catch (e) { toast.error(e.response?.data?.error || 'Errore'); }
                  finally { setProcessing(false); }
                }} disabled={processing}
                  className="w-full py-3 bg-yellow-600 text-white rounded-lg font-medium inline-flex items-center justify-center gap-2">
                  <CheckCircle size={18}/>{processing ? '...' : 'Invia in Revisione'}
                </button>
              </div>
            )}

            <div className="card-dark p-5">
              <label className="block text-xs text-gray-400 mb-1">Note</label>
              <textarea className="input-field-dark h-20 resize-y" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note per l'artista..."/>
              <button onClick={approve} disabled={processing}
                className="w-full mt-3 py-3 bg-green-600 text-white rounded-lg font-medium inline-flex items-center justify-center gap-2">
                <CheckCircle size={18}/>{processing ? '...' : 'Approva'}
              </button>
            </div>
            <div className="card-dark p-5">
              <label className="block text-xs text-gray-400 mb-1">Motivo rifiuto *</label>
              <textarea className="input-field-dark h-20 resize-y" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Spiega..."/>
              <button onClick={reject} disabled={processing}
                className="w-full mt-3 py-3 bg-red-600 text-white rounded-lg font-medium inline-flex items-center justify-center gap-2">
                <XCircle size={18}/>{processing ? '...' : 'Rifiuta'}
              </button>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
